import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translate } from './translate';
import es from './es';

/**
 * LANGUAGE (9/4/26) - English by default, Spanish on request.
 *
 * The choice lives in localStorage under LANG_KEY, so it survives a reload and a sign-out (logout
 * removes its own three keys, not this one) and applies to the login page before anyone signs in.
 * `<html lang>` follows it so screen readers and the browser's spell-checker switch too.
 *
 * NO PROVIDER = ENGLISH. The default context value is a working English translator, so a component
 * rendered on its own (every existing test does this) behaves exactly as it did before this layer
 * existed. That is deliberate: the tests assert English text, and they must keep passing unchanged.
 */
export const LANG_KEY = 'medeffects.lang';

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const DICTS = { en: null, es };

/**
 * The BCP 47 tag for dates that spell out a month or weekday. es-US: Spanish month names with US
 * conventions. Purely numeric dates (invoice due dates, sync times) keep en-US everywhere - a US
 * clinic reads 9/4/2026 as September 4th in either language, and es-US would flip it to 4/9/2026.
 */
export const DATE_LOCALES = { en: 'en-US', es: 'es-US' };
export function dateLocale(lang) {
  return DATE_LOCALES[lang] || DATE_LOCALES.en;
}

export function isSupportedLanguage(code) {
  return LANGUAGES.some((l) => l.code === code);
}

export function readStoredLanguage() {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    return isSupportedLanguage(stored) ? stored : 'en';
  } catch {
    return 'en';
  }
}

const englishT = (key, vars) => translate(null, key, vars);

// Exported for the one class component (ErrorBoundary), which reads it via static contextType.
export const LanguageContext = createContext({ lang: 'en', setLang: () => {}, t: englishT, locale: DATE_LOCALES.en });

export function LanguageProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() =>
    isSupportedLanguage(initialLang) ? initialLang : readStoredLanguage(),
  );

  const setLang = useCallback((code) => {
    if (!isSupportedLanguage(code)) return;
    setLangState(code);
    try {
      localStorage.setItem(LANG_KEY, code);
    } catch {
      // Storage disabled: the choice still applies for this page load.
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // One `t` per language, so it is a stable hook dependency and memoised lists do not rebuild on
  // every render.
  const t = useMemo(() => {
    const dict = DICTS[lang] || null;
    return (key, vars) => translate(dict, key, vars);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, locale: dateLocale(lang) }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/** The translator alone - what nearly every component wants. */
export function useT() {
  return useContext(LanguageContext).t;
}

/** The date locale alone - for the handful of dates that spell out a month or weekday. */
export function useLocale() {
  return useContext(LanguageContext).locale;
}
